import { format, parseISO } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { ConnectedFacility, ConnectionRequestStatus } from '../../../api';
import { SortDataPayload } from '../../models/sort';
import { getConnectionSenderName } from '../../utils/connections';
import { Avatar } from '../Avatar';
import { ConnectionRequestFilterState, SortBy } from '../ConnectionRequestsFilters';
import { ConnectionCombinedStatusBadge } from '../ConnectionStatusBadge';
import { MaxWidthElipsisBox } from '../MaxWidthElipsisBox';
import { ConnectionCombinedStatusEnum } from './ConnectionCombinedStatusEnum';
import { HederCell } from './HederCell';

const DATE_FORMAT = 'LLL d, yyyy';

export const useColumns = ({
  onAccept,
  onReject,
  onEntityCancel,
  filters,
  showPresentationStatus,
  onSetSort,
  isProvider = false,
  onRowClick,
}: {
  onReject: (row: ConnectedFacility) => void;
  onAccept: (row: ConnectedFacility) => void;
  onEntityCancel: (row: ConnectedFacility) => void;
  onSetSort: (data: SortDataPayload) => void;
  filters: ConnectionRequestFilterState;
  showPresentationStatus?: boolean;
  isProvider?: boolean;
  onRowClick: (row: ConnectedFacility) => void;
}) => {
  const onRejectHandler = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      connectionRequest: ConnectedFacility
    ) => {
      event.stopPropagation();
      onReject(connectionRequest);
    },
    [onReject]
  );

  const onAcceptHandler = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      connectionRequest: ConnectedFacility
    ) => {
      event.stopPropagation();
      onAccept(connectionRequest);
    },
    [onAccept]
  );

  return useMemo(() => {
    return [
      {
        header: <span className="heder-cell d-block">Action</span>,
        id: 'entity_action',
        accessorFn: (row) => row,
        cell: (props) => {
          const connectionRequest = props.getValue();

          return connectionRequest.status === ConnectionRequestStatus.PENDING_RESPONSE ? (
            <div className="d-flex">
              <Button
                variant="link"
                size="sm"
                className="text-danger p-0 me-2"
                onClick={(event) => {
                  event.stopPropagation();
                  onEntityCancel(connectionRequest);
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <></>
          );
        },
        size: 120,
        enableResizing: true,
        minSize: 120,
        maxSize: 120,
      },
      {
        header: '',
        id: 'provider_action',
        accessorFn: (row) => row,
        cell: (props) => {
          const connectionRequest = props.getValue();
          return (
            <div className="d-flex">
              <Button
                variant="outline-primary"
                size="sm"
                className="p-1"
                onClick={(event) => {
                  event.stopPropagation();
                  onRowClick(connectionRequest);
                }}
              >
                Open
              </Button>
            </div>
          );
        },
        size: 60,
        enableResizing: true,
        minSize: 60,
        maxSize: 60,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Facility"
            sortKey={SortBy.FACILITY_NAME}
          />
        ),
        id: 'facility',
        accessorFn: (row) => ({
          facility: row.facility,
        }),
        cell: (props) => {
          const { facility } = props.getValue();
          return <div className="text-section-name--dark-brand">{facility.name}</div>;
        },
        size: 200,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Location"
            sortKey={SortBy.FACILITY_STATE}
          />
        ),
        id: 'facility_location',
        accessorFn: (row) => {
          return {
            facility: row.facility,
          };
        },
        cell: (props) => {
          const { facility } = props.getValue();

          return (
            <>
              {facility.city}, {facility.state}
            </>
          );
        },
        size: 200,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Healthcare Organization"
            sortKey={SortBy.ENTITY_NAME}
          />
        ),
        id: 'entity_name',
        accessorKey: 'healthcare_entity',
        cell: (props) => {
          const healthcare_entity = props.getValue();
          return <span>{healthcare_entity?.name}</span>;
        },
        size: 200,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Provider"
            sortKey="provider__last_name"
          />
        ),
        accessorKey: 'provider',
        cell: (props) => {
          const provider = props.getValue();
          return (
            <div className="d-flex align-items-center text-section-name--dark-brand">
              <Avatar className="me-1" size={24} image={provider.profile_picture} />
              {provider.display_name}
            </div>
          );
        },
        size: 244,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },

      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Specialty"
            sortKey={SortBy.PROVIDER_SPECIALTY}
          />
        ),
        id: 'specialty',
        accessorFn: (row) => row,
        cell: (props) => {
          const row = props.getValue();
          let specialtiesText: string;

          // Check if there's an opportunity with specialty_display
          if (row.opportunity?.specialty_display) {
            specialtiesText = row.opportunity.specialty_display;
          } else {
            // Otherwise, use provider specialties joined with commas
            const providerSpecialties = row.provider?.specialties || [];
            specialtiesText = Array.isArray(providerSpecialties)
              ? providerSpecialties.join(', ')
              : '';
          }
          return (
            <MaxWidthElipsisBox className="d-inline-block" maxWidth="190px">
              {specialtiesText}
            </MaxWidthElipsisBox>
          );
        },
        size: 177,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: <HederCell text="Status" />,
        accessorKey: 'combined_status',
        id: 'combined_status',
        cell: (props) => {
          return (
            <ConnectionCombinedStatusBadge
              status={props.getValue() as ConnectionCombinedStatusEnum}
              userIsProvider={isProvider}
              className="p-2"
            />
          );
        },
        size: 225,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Sent by"
            sortKey={SortBy.HEALTHCARE_REP_LAST_NAME}
          />
        ),
        id: 'sender',
        accessorFn: (row) => row,
        cell: (props) => {
          const connectionRequest = props.getValue();
          return (
            <div className="d-flex align-items-center">
              <Avatar
                className="me-1"
                size={24}
                image={connectionRequest?.healthcareEntityRep?.user?.profile?.profile_picture}
              />
              <span className="fs-14">{getConnectionSenderName(connectionRequest)}</span>
            </div>
          );
        },
        size: 177,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
      {
        header: (
          <HederCell
            setSort={onSetSort}
            sortBy={filters.sortBy}
            sortDirection={filters.sortDirection}
            text="Last Updated"
            sortKey={SortBy.UPDATED_AT}
          />
        ),
        accessorKey: 'updated_at',
        cell: (props) => {
          return <>{format(parseISO(props.getValue()), DATE_FORMAT)}</>;
        },
        size: 177,
        enableResizing: true,
        minSize: 50,
        maxSize: 500,
      },
    ];
  }, [
    onSetSort,
    filters.sortBy,
    filters.sortDirection,
    showPresentationStatus,
    onEntityCancel,
    onRejectHandler,
    onAcceptHandler,
    isProvider,
    onRowClick,
  ]);
};
